import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Clock, Send, Loader2, CheckCircle2 } from "lucide-react";
import { BRAND, DEPARTMENTS } from "@/lib/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    department: "sales",
    subject: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setDone(true);
      toast.success("Inquiry received. Our team will reach out shortly.");
      setForm({ name: "", company: "", email: "", phone: "", department: "sales", subject: "", message: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail?.[0]?.msg || err?.response?.data?.detail || "Something went wrong.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <section className="pt-28 md:pt-36 pb-12 md:pb-16 border-b border-damita-border relative overflow-hidden">
        <div className="absolute inset-0 -z-10 blueprint-bg opacity-50" />
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16">
          <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-damita-blue mb-6 flex items-center gap-3">
            <span className="w-8 tick-line" /> Contact Us
          </div>
          <div className="grid grid-cols-12 gap-6 lg:gap-12 items-end">
            <h1 className="col-span-12 lg:col-span-8 font-display font-black text-4xl md:text-6xl lg:text-[88px] leading-[0.95] tracking-tighter">
              Let&apos;s power your<br />
              <span className="text-damita-blue">next project.</span>
            </h1>
            <p className="col-span-12 lg:col-span-4 text-base md:text-lg leading-relaxed text-damita-ink2">
              Discuss your power configuration or project tender with our technical experts
              today.
            </p>
          </div>
        </div>
      </section>

      <section data-testid="contact-section" className="border-b border-damita-border">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16 py-14 md:py-20">
          <div className="grid grid-cols-12 gap-px bg-damita-border border border-damita-border">
            {/* Left — Company / contact details */}
            <div className="col-span-12 lg:col-span-5 bg-white">
              <div className="p-6 md:p-8 border-b border-damita-border">
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-damita-ink2 mb-2">
                  Company
                </div>
                <div className="font-display font-bold text-lg md:text-xl tracking-tight">
                  {BRAND.full}
                </div>
                <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-damita-blue mt-1">
                  ({BRAND.short})
                </div>
              </div>

              <a
                data-testid="contact-phone"
                href={`tel:${BRAND.phoneHref}`}
                className="block p-6 md:p-8 border-b border-damita-border hover:bg-damita-surface transition-colors"
              >
                <div className="flex items-start gap-4">
                  <Phone size={18} className="text-damita-blue mt-1 shrink-0" />
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-damita-ink2 mb-1">
                      Mobile
                    </div>
                    <div className="font-display font-bold text-lg md:text-xl tracking-tight">
                      {BRAND.phone}
                    </div>
                  </div>
                </div>
              </a>

              <div className="p-6 md:p-8 border-b border-damita-border">
                <div className="flex items-start gap-4">
                  <MapPin size={18} className="text-damita-blue mt-1 shrink-0" />
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-damita-ink2 mb-1">
                      Head Office
                    </div>
                    <div className="text-base text-damita-ink leading-relaxed">
                      {BRAND.address.line1}<br />
                      {BRAND.address.line2}<br />
                      <span className="text-damita-ink2 text-sm">Landmark — {BRAND.address.landmark}</span><br />
                      <span className="font-mono text-[12px] mt-1 inline-block">PIN {BRAND.address.pin}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-6 md:p-8 border-b border-damita-border">
                <div className="flex items-start gap-4">
                  <Clock size={18} className="text-damita-blue mt-1 shrink-0" />
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-damita-ink2 mb-1">
                      Business Hours
                    </div>
                    <div className="font-display font-bold text-base md:text-lg tracking-tight">
                      {BRAND.hours}
                    </div>
                    <div className="text-sm text-damita-ink2 mt-1 inline-flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-green-500 inline-block" />
                      {BRAND.emergency}
                    </div>
                  </div>
                </div>
              </div>

              {/* Email departments */}
              {DEPARTMENTS.map((d) => (
                <a
                  key={d.id}
                  data-testid={`dept-${d.id}`}
                  href={`mailto:${d.email}`}
                  className="flex items-start gap-4 p-6 md:p-8 border-b border-damita-border last:border-b-0 hover:bg-damita-surface transition-colors group"
                >
                  <Mail size={18} className="text-damita-blue mt-1 shrink-0" />
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-damita-ink2 mb-1">
                      {d.label}
                    </div>
                    <div className="font-display font-bold text-base md:text-lg tracking-tight group-hover:text-damita-blue transition-colors break-all">
                      {d.email}
                    </div>
                  </div>
                </a>
              ))}
            </div>

            {/* Right — Form */}
            <div className="col-span-12 lg:col-span-7 bg-damita-surface p-6 md:p-10">
              {done ? (
                <div data-testid="contact-success" className="h-full flex flex-col items-start justify-center min-h-[420px]">
                  <div className="w-14 h-14 grid place-items-center bg-damita-blue text-white mb-6">
                    <CheckCircle2 size={26} />
                  </div>
                  <h3 className="font-display font-black text-3xl md:text-4xl tracking-tighter mb-3">
                    Inquiry received.
                  </h3>
                  <p className="text-damita-ink2 max-w-md text-base md:text-lg">
                    Thank you. Our team will reach out within one business day. For urgent
                    service requests please email{" "}
                    <a className="text-damita-blue underline" href="mailto:Service@bbpowersolutions.in">
                      Service@bbpowersolutions.in
                    </a>{" "}
                    or call <a className="text-damita-blue underline" href={`tel:${BRAND.phoneHref}`}>{BRAND.phone}</a>.
                  </p>
                  <button
                    data-testid="send-another"
                    onClick={() => setDone(false)}
                    className="mt-8 inline-flex items-center gap-2 bg-damita-blue text-white px-5 py-3 font-semibold hover:bg-damita-yellow transition-colors"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} className="grid grid-cols-2 gap-4 md:gap-5">
                  <Field label="Full Name *">
                    <input required data-testid="input-name" value={form.name} onChange={(e) => update("name", e.target.value)} className="input" placeholder="Your name" />
                  </Field>
                  <Field label="Company">
                    <input data-testid="input-company" value={form.company} onChange={(e) => update("company", e.target.value)} className="input" placeholder="Company / Organisation" />
                  </Field>
                  <Field label="Email *">
                    <input required type="email" data-testid="input-email" value={form.email} onChange={(e) => update("email", e.target.value)} className="input" placeholder="you@company.com" />
                  </Field>
                  <Field label="Phone">
                    <input data-testid="input-phone" value={form.phone} onChange={(e) => update("phone", e.target.value)} className="input" placeholder="+91 98xxxxxxxx" />
                  </Field>
                  <Field label="Department" full>
                    <select data-testid="input-department" value={form.department} onChange={(e) => update("department", e.target.value)} className="input">
                      {DEPARTMENTS.map((d) => (
                        <option key={d.id} value={d.id}>{d.label}</option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Subject" full>
                    <input data-testid="input-subject" value={form.subject} onChange={(e) => update("subject", e.target.value)} className="input" placeholder="e.g. 80kVA UPS for Guwahati DC" />
                  </Field>
                  <Field label="Message *" full>
                    <textarea required data-testid="input-message" rows={5} value={form.message} onChange={(e) => update("message", e.target.value)} className="input resize-none" placeholder="Brief us on your power requirement, site location and timeline." />
                  </Field>
                  <div className="col-span-2 flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-2">
                    <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-damita-ink2">
                      Your details are confidential. Used only to respond.
                    </p>
                    <button
                      data-testid="submit-inquiry"
                      type="submit"
                      disabled={loading}
                      className="inline-flex items-center gap-3 bg-damita-blue text-white px-6 py-4 font-semibold hover:bg-damita-blueHover transition-all btn-press disabled:opacity-60"
                    >
                      {loading ? <Loader2 size={18} className="animate-spin" /> : <Send size={16} />}
                      {loading ? "Sending..." : "Submit Inquiry"}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      <style>{`
        .input { width:100%; background:#fff; border:1px solid var(--damita-border); padding:14px 16px; font-size:15px; font-family:'IBM Plex Sans',sans-serif; color:var(--damita-ink); border-radius:0; outline:none; transition:border-color .15s, box-shadow .15s; }
        .input:focus { border-color: var(--damita-blue); box-shadow: 0 0 0 2px rgba(0,47,167,0.15); }
        .input::placeholder { color:#a1a1aa; }
      `}</style>
    </>
  );
}

function Field({ label, children, full }) {
  return (
    <label className={`block ${full ? "col-span-2" : "col-span-2 md:col-span-1"}`}>
      <span className="block font-mono text-[10px] uppercase tracking-[0.22em] text-damita-ink2 mb-2">{label}</span>
      {children}
    </label>
  );
}
